import { Component, inject, NgZone, OnInit, Type, afterNextRender, HostListener } from '@angular/core';
import { CommonModule, NgComponentOutlet } from '@angular/common';
import { RouterOutlet } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { MatDialog, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { filter, switchMap, take } from 'rxjs/operators';
import { GrantedPermitionsService } from './services/granted-permitions.service';
import { ZendeskService } from './services/zendesk.service';
import { IdleTimerService } from './services/idle-timer.service';
import { TenantWordingService } from './core/theme/tenant-wording.service';
import { TenantThemeService } from './core/theme/tenant-theme.service';
import { TranslocoService } from '@jsverse/transloco';
import { AvailableAmountService } from './shared/services/available-amount.service';
import { UnassignedDomainNoticeComponent } from './shared/components/unassigned-domain-notice/unassigned-domain-notice.component';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    MatDialogModule,
    NgComponentOutlet,
    UnassignedDomainNoticeComponent,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
  private dialogRef: MatDialogRef<unknown> | null = null;
  title = 'polipay-bank';
  isCollapsed = false;

  pushNotificationComponent: Type<unknown> | null = null;

  private zendeskService = inject(ZendeskService);
  private ngZone = inject(NgZone);
  private idleTimerService = inject(IdleTimerService);
  private titleService = inject(Title);
  private tenantWording = inject(TenantWordingService);
  private translocoService = inject(TranslocoService);
  private availableAmountService = inject(AvailableAmountService);
  private tenantTheme = inject(TenantThemeService);

  /**
   * El dominio pertenece a un nivel de marcas pero no está asignado a ninguna.
   * Vive en el componente RAÍZ y no en un guard de ruta: la condición es del
   * DOCUMENTO, no de una ruta, y un guard habría que registrarlo en cada una
   * —incluidas las futuras y la comodín—, así que una ruta nueva quedaría sin
   * protección en silencio. Acá el aviso reemplaza al `router-outlet` sin
   * navegar a ningún lado, y por eso da igual con qué ruta se entró (R8).
   *
   * La señal está resuelta a tiempo porque `loadTenantTheme()` corre como
   * inicializador de la app, antes del primer pintado.
   */
  readonly dominioNoAsignado = this.tenantTheme.dominioNoAsignado;

  /**
   * Lo que se espera antes de inyectar el widget de soporte. Es `public` porque
   * su spec lo consume: con el número suelto en el `setTimeout`, subir el
   * retraso dejaba la prueba del caso bloqueado pasando sin probar nada.
   */
  static readonly ZENDESK_INIT_DELAY_MS = 3000;

  private static readonly SPLASH_MIN_MS = 400;
  private static readonly SPLASH_FADE_MS = 400;
  private static readonly SPLASH_LOGO_MAX_WAIT_MS = 3000;

  constructor(
    public geo: GrantedPermitionsService,
    private dialog: MatDialog,
  ) {
    afterNextRender(() => {
      this.removeSplash();

      const runWhenIdle = () => {
        import('./shared/components/push-notification/push-notification.component').then((m) => {
          this.ngZone.run(() => {
            this.pushNotificationComponent = m.PushNotificationComponent;
          });
        });
      };
      if (typeof requestIdleCallback !== 'undefined') {
        requestIdleCallback(runWhenIdle, { timeout: 2000 });
      } else {
        setTimeout(runWhenIdle, 500);
      }
    });
  }

  private removeSplash(): void {
    const splash = document.getElementById('plp-splash');
    if (!splash) return;

    const logo = document.getElementById('plp-splash-logo') as HTMLImageElement | null;
    const start = (window as any).__PLP_SPLASH_START || Date.now();

    const doFade = () => {
      const elapsed = Date.now() - start;
      const remaining = Math.max(0, AppComponent.SPLASH_MIN_MS - elapsed);
      setTimeout(() => {
        splash.classList.add('fade-out');
        setTimeout(() => {
          splash.remove();
          document.getElementById('plp-splash-styles')?.remove();
        }, AppComponent.SPLASH_FADE_MS);
      }, remaining);
    };

    // Si el logo ya cargó o es local, fade de inmediato
    if (!logo || logo.complete) {
      doFade();
      return;
    }

    // Esperar a que el logo del CDN cargue (con timeout máximo)
    let resolved = false;
    const resolve = () => {
      if (resolved) return;
      resolved = true;
      doFade();
    };

    logo.addEventListener('load', resolve, { once: true });
    logo.addEventListener('error', resolve, { once: true });
    setTimeout(resolve, AppComponent.SPLASH_LOGO_MAX_WAIT_MS);
  }

  ngOnInit(): void {
    // ⚠️ Bajo el bloqueo NO arranca NADA del shell, y el corte va acá arriba
    // porque lo que sigue no vive dentro de la plantilla: el `@else` no lo tapa.
    //
    //  · el modal de geolocalización se abre en el overlay de Material, con
    //    `disableClose: true` y un solo botón ("usar mi ubicación"), así que
    //    quedaba ENCIMA del aviso, con trampa de foco y sin salida — y de paso
    //    el navegador pedía GPS en un dominio que no es de nadie. Un visitante
    //    nuevo cae siempre ahí: el permiso es por origen, y en un dominio recién
    //    dado de baja nadie tiene el permiso concedido;
    //  · el chat de soporte inyecta un `<script>` de un tercero que identifica a
    //    quien opera la plataforma, sobre una pantalla que tiene que ser neutra
    //    y no revelar el inquilinato;
    //  · el título de la pestaña se pondría en el `appName` de la marca base.
    //    Lo pone el propio aviso, con su texto traducido.
    if (this.dominioNoAsignado()) return;

    // ⚠️ SOLO si hay nombre. Este es el SEGUNDO escritor del título (el otro es
    // `TenantThemeService.applyWording`) y corre después, así que un valor vacío
    // acá pisaba el que ya estaba bien y dejaba la pestaña mostrando la URL.
    const nombreDeLaMarca = this.tenantWording.get('appName');
    if (nombreDeLaMarca) {
      this.titleService.setTitle(nombreDeLaMarca);
    }

    // Esperar a que Transloco cargue las traducciones antes de escuchar permisos de geo
    this.translocoService.selectTranslation().pipe(
      take(1),
      switchMap(() => this.geo.estadoPermiso$),
      filter((estado): estado is string => estado != null && estado.length > 0),
    ).subscribe((estado) => {
      if (estado === 'granted') {
        if (this.dialogRef) {
          this.dialogRef.close();
          this.dialogRef = null;
        }
        return;
      }
      if (estado === 'denied' || estado === 'prompt') {
        this.mostrarAlertaBloqueo();
      }
    });

    setTimeout(() => {
      this.zendeskService.initZendesk();
    }, AppComponent.ZENDESK_INIT_DELAY_MS);
  }

  mostrarAlertaBloqueo(): void {
    if (this.dialogRef) return;

    import(
      './shared/components/warning-modal/warning-modal.component'
    ).then((m) => {
      const component = m.WarningModalComponent;
      this.ngZone.run(() => {
        this.dialogRef = this.dialog.open(component, {
          width: '400px',
          disableClose: true,
          data: {
            title: this.translocoService.translate('errors.location.title'),
            message: this.translocoService.translate('errors.location.message'),
            messageBtn: this.translocoService.translate('errors.location.useMyLocation'),
            showButton: true,
            isLocation: true,
          },
        });
        this.dialogRef.afterClosed().subscribe(() => {
          this.dialogRef = null;
        });
      });
    });
  }

  toggleSidebar() {
    this.isCollapsed = !this.isCollapsed;
  }

  private lastBalanceRefresh = 0;
  private readonly BALANCE_REFRESH_THROTTLE_MS = 30000;

  @HostListener('document:visibilitychange')
  onVisibilityChange(): void {
    if (document.visibilityState !== 'visible') return;
    const now = Date.now();
    if (now - this.lastBalanceRefresh < this.BALANCE_REFRESH_THROTTLE_MS) return;
    this.lastBalanceRefresh = now;
    this.availableAmountService.refreshBalance();
  }
}